/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';

/**
 * @prop {String} color - Defines the color of the segment outline. If left undefined the outline will be transparent when the segment is idle and UI 0 when it is focused.
 * @prop {Boolean} focus - If set to `true`, the segment will have a solid outline with 2px thickness.
 * @prop {Boolean} hideOverlay - If set to `true`, the content inside the side and corner slots will be hidden.
 * @attr {Boolean} hide-overlay - If set to true, the content inside the side and corner slots will be hidden.
 * @slot - The default slot which holds a functional viewing segment such as an img or a canvas.
 * @slot right - Take sh-toolbar as a child, which hosts elements such as tools or slider.
 * @slot left - Take sh-toolbar as a child, which hosts elements such as tools or slider.
 * @slot top - Take sh-toolbar as a child, which hosts elements such as tools or slider.
 * @slot bottom - Take sh-toolbar as a child, which hosts elements such as tools or slider.
 * @slot top-right - Take sh-image-text as children elements.
 * @slot top-left - Take sh-image-text as children elements.
 * @slot bottom-right - Take sh-image-text as children elements.
 * @slot bottom-left - Take sh-image-text as children elements.
 */
export class SHImageSegment extends LitElement {
  static get properties() {
    return {
      color: { type: String, reflect: true },
      focus: { type: Boolean, reflect: true },
      hideOverlay: { type: Boolean, reflect: true, attribute: 'hide-overlay' },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          position: relative;
          border-radius: 0.125rem;
          background: black;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        /* slotted image */
        :host > ::slotted(*:not([slot])) {
          height: 100%;
          width: 100%;
          flex: 1;
        }
        /* outlines */
        .segment-outline {
          position: absolute;
          top: 0.125rem;
          left: 0.125rem;
          height: calc(100% - 0.25rem);
          width: calc(100% - 0.25rem);
          box-sizing: border-box;
          border: 0.0625rem solid transparent;
          border-radius: 0.125rem;
          transition: var(--transition-time) all ease-in-out;
          opacity: var(--opacity-3);
        }
        :host(:not([outline])[focus]) .segment-outline {
          border-color: rgb(var(--ui-0));
        }
        :host([focus]) .segment-outline {
          border-width: 0.125rem;
          opacity: var(--opacity-2);
        }
        /* slots */
        :host ::slotted(*[slot]) {
          pointer-events: all;
        }
        .corner,
        .side {
          pointer-events: none;
          position: absolute;
          height: auto;
          width: auto;
          transition: var(--transition-time) all ease-in-out;
        }
        :host([hide-overlay]) .corner,
        :host([hide-overlay]) .side {
          opacity: 0;
          pointer-events: none;
        }
        /* corner slots */
        .corner {
          max-width: calc(50% - 0.75rem);
          display: flex;
          flex-direction: column;
        }
        .corner > ::slotted(*) {
          display: flex;
          flex-direction: column;
        }
        .corner.right ::slotted(*) {
          text-align: right;
          margin-left: 0.5rem;
        }
        /* side slots */
        .side {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .side.left > ::slotted(*),
        .side.right > ::slotted(*) {
          margin: 0.25rem 0;
        }
        .side.left,
        .side.right {
          height: calc(100% - 1.5rem);
          flex-direction: column;
        }
        .side.top > ::slotted(*),
        .side.bottom > ::slotted(*) {
          margin: 0 0.25rem;
        }
        .side.top,
        .side.bottom {
          width: calc(100% - 1.5rem);
          flex-direction: row;
        }
        .left {
          left: 0.75rem;
        }
        .right {
          right: 0.75rem;
        }
        .top {
          top: 0.75rem;
        }
        .bottom {
          bottom: 0.75rem;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="segment-outline" style="border-color: ${this.color}"></div>
      <!-- corner slots -->
      <div class="corner top left">
        <slot name="top-left"></slot>
      </div>
      <div class="corner top right">
        <slot name="top-right"></slot>
      </div>
      <div class="corner bottom left">
        <slot name="bottom-left"></slot>
      </div>
      <div class="corner bottom right">
        <slot name="bottom-right"></slot>
      </div>
      <!-- side slots -->
      <div class="side left">
        <slot name="left"></slot>
      </div>
      <div class="side right">
        <slot name="right"></slot>
      </div>
      <div class="side top">
        <slot name="top"></slot>
      </div>
      <div class="side bottom">
        <slot name="bottom"></slot>
      </div>
      <!-- image slot -->
      <slot></slot>
    `;
  }
}

customElements.define('sh-image-segment', SHImageSegment);
